import _ from "lodash";
import { isNewVideoMeetingUIFeatureEnabled } from "../../components/EventModalV2/modules/Utils";

/**
 * Mapping for original locale keys (used when feature flag is disabled)
 * Maps base keys to their original locale key names
 */
const LOCALE_KEY_MAPPING = {
  // getSubText keys
  repeats_every_label_until_end: "repeats_every_label_until_end",
  // "Repeats every {{label}} until {{academicYearEndDate}}"
  school_day: "school_day",
  // "school day"
  week_on_label: "week_on_label",
  // "week on {{dayName}}"
  event_two_weeks_on_label: "event_two_weeks_on_label",
  // "2 weeks on {{dayName}}"
  repeats_on_label_until_end: "repeats_on_label_until_end",
  // "Repeats on the {{label}} of every month until {{academicYearEndDate}}"
  date_of_month: "date_of_month",
  // "{{day}}{{ordinal}}"
  day_of_month: "day_of_month",
  // "{{week}}{{ordinal}} {{dayName}}"
  event_every_last: "event_every_last",
  // "every last {{dayName}}"

  // getSummaryText - DAILY keys
  day: "day",
  // "day"
  day_unit: "day_unit",
  // "days"
  repeat_every_day: "repeat_every_day",
  // "Repeat every day"
  event_every_count_days: "event_every_count_days",
  // "Every {{count}} days"
  repeats_every_count_until_end_academic_year:
    "repeats_every_count_until_end_academic_year",
  // "Repeats every {{label}}"
  repeats_every_count_until_date: "repeats_every_count_until_date",
  // "Repeats every {{label}} until {{endDate}}"
  repeats_every_count_ends_after_occurrences:
    "repeats_every_count_ends_after_occurrences",
  // "Repeats every {{label}} ends after {{occurrences}} occurrences"
  repeats_every_count_ends_after_occurrence:
    "repeats_every_count_ends_after_occurrence",
  // "Repeats every {{label}} ends after {{occurrences}} occurrence"

  // getSummaryText - WEEKLY keys
  week: "week",
  // "week"
  week_unit: "week_unit",
  // "weeks"
  repeat_every_weeks_on_day: "repeat_every_weeks_on_day",
  // "Repeat every week on {{dayName}}"
  event_every_count_weeks_on: "event_every_count_weeks_on",
  // "Every {{count}} weeks on {{dayName}}"
  repeats_every_count_on_day_until_date:
    "repeats_every_count_on_day_until_date",
  // "Repeats every {{label}} on {{day}} until {{endDate}}"
  repeats_every_count_on_day_ends_after_occurrences:
    "repeats_every_count_on_day_ends_after_occurrences",
  // "Repeats every {{label}} on {{day}} ends after {{occurrences}} occurrences"
  repeats_every_count_on_day_ends_after_occurrence:
    "repeats_every_count_on_day_ends_after_occurrence",
  // "Repeats every {{label}} on {{day}} ends after {{occurrences}} occurrence"
  repeat_every_weeks_day: "repeat_every_weeks_day",
  // " Repeat every week on {{dayName}}"

  // getSummaryText - MONTHLY keys
  month: "month",
  // "month"
  month_unit: "month_unit",
  // "months"
  last: "last",
  // "last"
  repeat_every_months_on_day_count: "repeat_every_months_on_day_count",
  // "Repeat every month on {{count}}"
  event_every_count_months_on_day: "event_every_count_months_on_day",
  // "Every {{count}} months on the {{day}}"
  repeats_every_count_on_month_until_end_academic_year:
    "repeats_every_count_on_month_until_end_academic_year",
  // "Repeats every {{label}} on the {{month}} day"
  repeats_every_count_on_month_until_date:
    "repeats_every_count_on_month_until_date",
  // "Repeats every {{label}} on the {{month}} day until {{endDate}}"
  repeats_every_count_on_month_ends_after_occurrences:
    "repeats_every_count_on_month_ends_after_occurrences",
  // "Repeats every {{label}} on the {{month}} day ends after {{occurrences}} occurrences"
  repeats_every_count_on_month_ends_after_occurrence:
    "repeats_every_count_on_month_ends_after_occurrence",
  // "Repeats every {{label}} on the {{month}} day ends after {{occurrences}} occurrence"

  // getRepeatEventOptions keys
  event_school_day: "event_school_day",
  // "Every school day"
  event_weekly_on: "event_weekly_on",
  // "Weekly on {{dayName}}"
  event_every_2_weeks_on: "event_every_2_weeks_on",
  // "Every 2 weeks on {{dayName}}"
  event_every_nth_day: "event_every_nth_day",
  // "Every {{day}}{{ordinal}} day of the month"
  event_every_nth_weekday: "event_every_nth_weekday",
  // "Every {{week}}{{ordinal}} {{dayName}} of the month"
  event_every_last_weekday: "event_every_last_weekday",
  // "Every last {{dayName}} of the month"
  event_custom: "event_custom",
  // "Custom"

  // getTabs keys
  monthly_on_day_label: "monthly_on_day_label",
  // "Monthly on day {{label}}"
  monthly_on_week_label: "monthly_on_week_label",
  // "Monthly on {{label}} {{week}}"

  // getErrorText keys
  error_text: "error_text",
  // "{{dayName}} is a non operational day for your school and event shall not be scheduled "
};

/**
 * Mapping for meeting context where "Repeats:" label is already shown in UI
 * Used when feature flag is enabled
 * Spreads LOCALE_KEY_MAPPING and overrides only keys that need different text for meeting context
 * Locale strings remove "Repeat/Repeats" prefix to avoid redundancy
 * Example: UI shows "Repeats:" + locale text "Every week on Monday" = "Repeats: Every week on Monday"
 */
const LOCALE_KEY_MAPPING_NEW_MEETING = {
  ...LOCALE_KEY_MAPPING,

  // getSubText keys - overrides for meeting context
  repeats_every_label_until_end: "every_label_until_end_meeting_v2",
  // "Every {{label}} until {{academicYearEndDate}}"
  repeats_on_label_until_end: "on_label_until_end_meeting_v2",
  // "On the {{label}} of every month until {{academicYearEndDate}}"

  // getSummaryText - DAILY keys - overrides for meeting context
  repeat_every_day: "every_day_meeting_v2",
  // "Every day"
  repeats_every_count_until_end_academic_year:
    "every_count_until_end_academic_year_meeting_v2",
  // "Every {{label}}"
  repeats_every_count_until_date: "every_count_until_date_meeting_v2",
  // "Every {{label}} until {{endDate}}"
  repeats_every_count_ends_after_occurrences:
    "every_count_ends_after_occurrences_meeting_v2",
  // "Every {{label}}, ends after {{occurrences}} occurrences"
  repeats_every_count_ends_after_occurrence:
    "every_count_ends_after_occurrence_meeting_v2",
  // "Every {{label}}, ends after {{occurrences}} occurrence"

  // getSummaryText - WEEKLY keys - overrides for meeting context
  repeat_every_weeks_on_day: "every_weeks_on_day_meeting_v2",
  // "Every week on {{dayName}}"
  repeats_every_count_on_day_until_date:
    "every_count_on_day_until_date_meeting_v2",
  // "Every {{label}} on {{day}} until {{endDate}}"
  repeats_every_count_on_day_ends_after_occurrences:
    "every_count_on_day_ends_after_occurrences_meeting_v2",
  // "Every {{label}} on {{day}}, ends after {{occurrences}} occurrences"
  repeats_every_count_on_day_ends_after_occurrence:
    "every_count_on_day_ends_after_occurrence_meeting_v2",
  // "Every {{label}} on {{day}}, ends after {{occurrences}} occurrence"
  repeat_every_weeks_day: "every_weeks_day_meeting_v2",
  // "Every week on {{dayName}}"

  // getSummaryText - MONTHLY keys - overrides for meeting context
  repeat_every_months_on_day_count: "every_months_on_day_count_meeting_v2",
  // "Every month on {{count}}"
  repeats_every_count_on_month_until_end_academic_year:
    "every_count_on_month_until_end_academic_year_meeting_v2",
  // "Every {{label}} on the {{month}} day"
  repeats_every_count_on_month_until_date:
    "every_count_on_month_until_date_meeting_v2",
  // "Every {{label}} on the {{month}} day until {{endDate}}"
  repeats_every_count_on_month_ends_after_occurrences:
    "every_count_on_month_ends_after_occurrences_meeting_v2",
  // "Every {{label}} on the {{month}} day, ends after {{occurrences}} occurrences"
  repeats_every_count_on_month_ends_after_occurrence:
    "every_count_on_month_ends_after_occurrence_meeting_v2",
  // "Every {{label}} on the {{month}} day, ends after {{occurrences}} occurrence"
};

/**
 * Get the appropriate locale key based on feature flag
 * When feature flag is enabled, uses meeting-specific locale mapping for all contexts
 * When feature flag is disabled, uses standard v2 locale mapping
 *
 * @param {string} baseKey - The base locale key (without namespace)
 * @returns {string} - The mapped key based on feature flag state, or original key if not found
 */
export const getLocaleKey = ({ baseKey, useNewLocale = false }) => {
  if (isNewVideoMeetingUIFeatureEnabled() && useNewLocale) {
    return _.get(LOCALE_KEY_MAPPING_NEW_MEETING, baseKey, baseKey);
  }
  return _.get(LOCALE_KEY_MAPPING, baseKey, baseKey);
};

/**
 * Get the full calendar locale key with namespace
 * @param {string} baseKey - The base locale key (without namespace)
 * @returns {string} - The full locale key with calendar namespace
 */
export const getCalendarLocaleKey = (baseKey, useNewLocale = false) => {
  return `calendar:${getLocaleKey({ baseKey, useNewLocale })}`;
};
